import * as Rx from 'rxjs';
import * as RxOp from 'rxjs/operators';
import { getPosApiUrl, getAccessToken } from '../../utils/state';
import {
  replaceAlerts,
  clearAlerts,
} from '../../components/alert-bar/AlertBar.state';
import {
  makePayment,
  cancelTransaction,
  getPaymentById,
  fetchServiceFeeRequest,
  fetchConvenienceFeeRequest,
  postVisitId,
  pollByPaymentIdentifier,
} from '../../utils/api/client';
import {
  changeDueModal,
  OPEN_CHANGE_DUE_MODAL,
} from '../order-details/OrderDetails.state';
import {
  checkoutAlertBarAction,
  checkoutModalAction,
  paymentSuccess,
  paymentError,
  INITIATE_PAYMENT,
  CANCEL_TRANSACTION_REQUEST,
  cancelTransactionSuccess,
  cancelTransactionFailure,
  INITIATE_CHECK_SCAN,
  isPollingForCheckResponse,
  stopAwaitingCreditCard,
  makeCheckPaymentError,
  IS_AWAITING_CREDIT_CARD,
  STOP_AWAITING_CREDIT_CARD,
  getPaymentByIdSuccess,
  stopCheckScanPoll,
  STOP_CHECK_SCAN_POLL,
  getPaymentByIdError,
  setInsertCheckTimeRemaining,
  checkScanMaxAttempts,
  setAwaitCreditCardTimeRemaining,
  cancelTransactionRequest,
  maxSecondsToInsertCard,
  maxSecondsToInsertCardDps,
  getServiceFeeSuccess,
  getServiceFeeError,
  GET_SERVICE_FEE,
  GET_CONVENIENCE_FEE,
  getConvenienceFeeSuccess,
  getConvenienceFeeError,
  INITIATE_PAYLINK,
  paylinkError,
  paylinkSuccess,
  isCheckProcessing,
  IS_POLLING_FOR_CHECK_RESPONSE,
  isPollingTimeExtended,
  retryInitiatePostVisitId,
  INITIATE_POST_VISIT,
  postVisitIdSuccess,
  postVisitIdError,
  RETRY_INITIATE_POST_VISIT,
  startPollingForCCResponse,
  stopPollingForCCResponse,
  pollingCCSuccess,
  pollingCCError,
  setPaymentUniqueIdentifier,
  START_POLLING_FOR_CC_RESPONSE,
  STOP_POLLING_FOR_CC_RESPONSE,
  setCheckScanFeedback,
} from './Checkout.state';
import { caughtError } from '../../utils/api/errorHandler';
import { closeModal } from '../../components/modal/Modal.state';
import { formatPrice, isDatacapTransaction } from '../../utils/helpers';
import {
  clearCart,
  getUpdatedCartRequest,
  GET_UPDATED_CART_SUCCESS,
} from '../cart/Cart.state';
import {
  ORDER_STATUS,
  TRANSACTION_STATUS,
  CASH,
  CHECK,
  CARD,
  WIRE,
  PAYLINK,
} from '../../utils/constants';
import { PAYMENT_TYPE } from '../../utils/constants';
import { EMPTY_ACTION } from '../../state';
import { resetPinPadRequest } from '../../state/shared';
import {
  getOrCreateTraceNumber,
  clearTraceStateFromStorage,
  extractLastFourFromTrack,
  markResponseReceived,
  markAttemptIndeterminate,
  getPendingPAUI,
  clearPendingPAUI,
} from '../../utils/traceNumber';

const PAYLINK_SENT_STATUS = 'paylink_sent';
const CHECKOUT_SCREEN_PATH = '/pos/checkout';

const caughtCheckoutError = (state) =>
  caughtError(checkoutAlertBarAction, 'Checkout Error:', state);

const caughtCancelError = (state) =>
  caughtError(checkoutAlertBarAction, 'Cancel Transaction Error:', state);

const makePaymentParams = (state, { payload }) => {
  const isPayerNameRequired =
    payload.hasOwnProperty('first_name') && payload.hasOwnProperty('last_name');
  let paymentParams;
  switch (payload.payment_tender_type) {
    case CASH:
      paymentParams = {
        ...(isPayerNameRequired && {
          first_name: payload.first_name,
          last_name: payload.last_name,
        }),
        payment_tender_type: payload.payment_tender_type,
        amount_given: payload.amount_given,
        change_due: payload.change_due,
        payment_delivery_type: payload.payment_delivery_type,
      };
      break;
    case CARD:
      if (payload.manual_entry) {
        paymentParams = {
          amount_given: payload.amount_given,
          service_fee: payload.service_fee,
          convenience_fee: payload.convenience_fee,
          payment_tender_type: payload.payment_tender_type,
          credit_card: {
            first_name: payload.first_name,
            last_name: payload.last_name,
            number: payload.number,
            month: payload.month,
            year: payload.year,
            cvv: payload.cvv,
            zipcode: payload.zip_code,
            manual_entry: payload.manual_entry,
          },
          visit_id: payload.visit_id,
        };
      } else {
        paymentParams = {
          ...(isPayerNameRequired && {
            first_name: payload.first_name,
            last_name: payload.last_name,
          }),
          acct_no: payload.acct_no,
          payment_tender_type: payload.payment_tender_type,
          amount_given: payload.amount_given,
          service_fee: payload.service_fee,
          convenience_fee: payload.convenience_fee,
          ...(payload.hasOwnProperty('full_track_data') && {
            full_track_data: payload.full_track_data,
          }),
          visit_id: payload.visit_id,
        };
      }

      // Generate or retrieve trace number for card payments
      // Extract card last four for trace number comparison (legacy path card-swap detection)
      const cardLastFour =
        paymentParams.credit_card?.number?.slice(-4) ??
        extractLastFourFromTrack(paymentParams.full_track_data);

      // Extract comparable payment parameters for trace number logic
      const comparableParams = {
        payment_tender_type: paymentParams.payment_tender_type,
        amount_given: paymentParams.amount_given,
        acct_no: paymentParams.acct_no,
        visit_id: paymentParams.visit_id,
        service_fee: paymentParams.service_fee,
        convenience_fee: paymentParams.convenience_fee,
        card_last_four: cardLastFour,
      };
      paymentParams.trace_number = getOrCreateTraceNumber(comparableParams);
      break;
    case CHECK:
      paymentParams = {
        payment_tender_type: payload.payment_tender_type,
        amount_given: payload.amount_given,
        submission_type: payload.submission_type,
        check: {
          first_name: payload.first_name,
          last_name: payload.last_name,
          check_type: payload.check_type,
          bank_account_type: payload.account_type,
          ...(payload.hasOwnProperty('company_name') && {
            company_name: payload.company_name,
          }),
          ...(payload.hasOwnProperty('isManualEntry') && {
            check_number: payload.check_number,
            bank_routing_number: payload.routing_number,
            bank_account_number: payload.account_number,
          }),
        },
      };
      break;
    case PAYLINK:
      paymentParams = {
        amount_given: payload.amount_given,
        payment_tender_type: payload.payment_tender_type,
        invoice_types: payload.invoice_types,
        phone: payload.phone,
        email: payload.email,
      };
      break;
    case WIRE:
      paymentParams = {
        payment_tender_type: payload.payment_tender_type,
        amount_given: payload.amount_given,
        wire_payment: {
          name: payload.name,
          description: payload.description,
          effective_date: payload.effective_date,
        },
      };
      break;
  }
  return {
    url: getPosApiUrl(state),
    token: getAccessToken(state),
    method: 'POST',
    payment: paymentParams,
  };
};

const getPaymentByIdParams = (state, action) => ({
  url: getPosApiUrl(state),
  token: getAccessToken(state),
  id: action.payload.data.id,
  method: 'GET',
});

const postVisitIdParams = (state, action) => ({
  url: getPosApiUrl(state),
  token: getAccessToken(state),
  order_id: action.payload,
  method: 'POST',
});

const pollByPaymentIdentifierParams = (state) => ({
  url: getPosApiUrl(state),
  token: getAccessToken(state),
  paymentAttemptUniqueId: state.checkout.paymentAttemptUniqueId,
  method: 'GET',
});

export const postVisitIdEpic = (action$, state$) =>
  action$.ofType(INITIATE_POST_VISIT).pipe(
    RxOp.flatMap((action) =>
      Rx.from(postVisitId(postVisitIdParams(state$.value, action))).pipe(
        RxOp.flatMap((response) => Rx.of(postVisitIdSuccess(response.data))),
        RxOp.catchError(() =>
          Rx.of(
            retryInitiatePostVisitId({
              order_id: action.payload,
              retryNumber: 1,
            }),
          ),
        ),
      ),
    ),
  );

export const retryPostVisitIdEpic = (action$, state$) =>
  action$.ofType(RETRY_INITIATE_POST_VISIT).pipe(
    RxOp.flatMap((action) =>
      Rx.from(
        postVisitId(
          postVisitIdParams(state$.value, {
            ...action,
            payload: action.payload.order_id,
          }),
        ),
      ).pipe(
        RxOp.flatMap((response) => Rx.of(postVisitIdSuccess(response.data))),
        RxOp.catchError((err) => {
          const numberOfRetries = action?.payload?.retryNumber;
          if (numberOfRetries && numberOfRetries < 3) {
            return Rx.of(
              retryInitiatePostVisitId({
                order_id: action.payload.order_id,
                retryNumber: numberOfRetries + 1,
              }),
            );
          } else {
            return Rx.of(postVisitIdError(err.message));
          }
        }),
      ),
    ),
  );

export const orderStatusResponseEpic = (action$, state$) =>
  action$.ofType(GET_UPDATED_CART_SUCCESS).pipe(
    RxOp.flatMap((action) => {
      const isOnCheckoutScreen =
        action.payload.location?.pathname === CHECKOUT_SCREEN_PATH;
      const isComplete =
        state$.value.cart.order.status === ORDER_STATUS.COMPLETED;
      const isPayLinkSent =
        state$.value.cart.order.status === PAYLINK_SENT_STATUS;
      if (isOnCheckoutScreen && (isComplete || isPayLinkSent)) {
        action.payload.navigate(
          `/pos/order/${state$.value.cart.order.order_id}`,
        ); // redirect to completed order details page

        // Clear any stale trace state (trace_number, tombstones, pendingPAUI)
        // so the next order starts fresh. Without this, a pendingPAUI from a
        // timed-out DPS attempt could persist if the order was completed via
        // a non-card payment (cash, check), causing a spurious resume-poll
        // on the next order's first card payment.
        clearTraceStateFromStorage();

        return Rx.of(
          checkoutModalAction(closeModal()),
          /*
          clear cart because the user fully paid for their order and it is now of status 'completed'.
          at the time the cart is cleared the user will be on the completed order details page from the redirect above,
          so they still have information about the order they just paid for.
          */
          clearCart(),
          state$.value.checkout?.isAwaitingCreditCard
            ? stopAwaitingCreditCard()
            : EMPTY_ACTION,
          state$.value.checkout?.isPollingForCCResponse
            ? stopPollingForCCResponse()
            : EMPTY_ACTION,
        );
      } else {
        return Rx.of(checkoutModalAction(closeModal()));
      }
    }),
  );

export const sendPayLinkEpic = (action$, state$) =>
  action$.ofType(INITIATE_PAYLINK).pipe(
    RxOp.flatMap((action) =>
      Rx.from(makePayment(makePaymentParams(state$.value, action))).pipe(
        RxOp.flatMap((response) => {
          const { status } = response;
          return Rx.of(
            status === 'success'
              ? paylinkSuccess(response)
              : paylinkError(new Error('PayLink Error')),
            getUpdatedCartRequest({
              id: state$.value.cart.order.order_id,
              navigate: action.payload.navigate,
              location: action.payload.location,
            }),
          );
        }),
        RxOp.catchError((err) =>
          caughtCheckoutError(state$.value)(
            err,
            paylinkError(err.message),
            getUpdatedCartRequest({
              id: state$.value.cart.order.order_id,
              navigate: action.payload.navigate,
              location: action.payload.location,
            }),
          ),
        ),
      ),
    ),
  );

// DPS: PAUI returned with success status — start polling.
const handleDpsPollStart = (identifier, action) =>
  Rx.of(
    setPaymentUniqueIdentifier({
      paymentAttemptUniqueId: identifier,
    }),
    startPollingForCCResponse({
      navigate: action.payload.navigate,
      location: action.payload.location,
    }),
  );

// DPS: PAUI returned with non-success — write tombstone and show error.
const handleDpsNonSuccess = (identifier, response, action, state$) => {
  if (identifier.length > 0) {
    markResponseReceived({ lastSeenPAUI: identifier });
  }
  return Rx.of(
    paymentError(response.message),
    checkoutAlertBarAction(
      replaceAlerts({
        heading: 'Payment Status:',
        text: response.message,
        variant: 'error',
      }),
    ),
    getUpdatedCartRequest({
      id: state$.value.cart.order.order_id,
      navigate: action.payload.navigate,
      location: action.payload.location,
    }),
  );
};

// Legacy / synchronous: completed payment result (no PAUI).
const handleSynchronousPaymentResponse = (
  response,
  action,
  state$,
  isDatacap,
) => {
  if (!response.data) {
    return Rx.of(paymentError('Unexpected payment response: no data returned'));
  }
  const { status, payment_type, amount_given, amount_processed, change_due } =
    response.data;
  let variant = status === 'successful' ? 'success' : 'error';
  let alertMessage = `${payment_type} ${status}`;

  // Clear all trace state on successful card payment.
  // This enables split tender scenarios (new trace number for next payment).
  if (status === 'successful' && payment_type === PAYMENT_TYPE.CARD_PAYMENT) {
    clearTraceStateFromStorage();
  }

  if (
    payment_type !== PAYMENT_TYPE.CASH_PAYMENT &&
    amount_given !== amount_processed
  ) {
    variant = 'warn';
    alertMessage = `${alertMessage}, however only ${formatPrice(
      amount_processed,
    )} was authorized for the payment.`;
  }
  return Rx.of(
    paymentSuccess(response),
    payment_type !== PAYMENT_TYPE.CASH_PAYMENT &&
      amount_given !== amount_processed
      ? checkoutAlertBarAction(
          replaceAlerts({
            heading: `Payment Status:`,
            text: alertMessage,
            variant: variant,
          }),
        )
      : EMPTY_ACTION,
    isDatacap
      ? resetPinPadRequest(
          state$.value.terminalLogin.terminalSession.terminal_id,
        )
      : EMPTY_ACTION,
    getUpdatedCartRequest({
      id: state$.value.cart.order.order_id,
      navigate: action.payload.navigate,
      location: action.payload.location,
    }),
    change_due ? changeDueModal(OPEN_CHANGE_DUE_MODAL) : EMPTY_ACTION,
    state$.value.checkout?.isAwaitingCreditCard
      ? stopAwaitingCreditCard()
      : EMPTY_ACTION,
  );
};

// Routes a makePayment response to the appropriate handler.
const handleMakePaymentResponse = (response, action, state$, isDatacap) => {
  const identifier = response?.data?.payment_attempt_unique_identifier;
  if (identifier) {
    if (
      identifier.length > 0 &&
      identifier !== state$.value.checkout.paymentAttemptUniqueId &&
      response.status === 'success'
    ) {
      return handleDpsPollStart(identifier, action);
    } else {
      return handleDpsNonSuccess(identifier, response, action, state$);
    }
  } else {
    return handleSynchronousPaymentResponse(
      response,
      action,
      state$,
      isDatacap,
    );
  }
};

const handleMakePaymentError = (err, action, state$, isDatacap) =>
  caughtCheckoutError(state$.value)(
    err,
    paymentError(err.message),
    state$.value.checkout?.isAwaitingCreditCard
      ? stopAwaitingCreditCard()
      : EMPTY_ACTION,
    state$.value.checkout?.isPollingForCCResponse
      ? stopPollingForCCResponse()
      : EMPTY_ACTION,
    isDatacap
      ? resetPinPadRequest(
          state$.value.terminalLogin.terminalSession.terminal_id,
        )
      : EMPTY_ACTION,
    getUpdatedCartRequest({
      id: state$.value.cart.order.order_id,
      navigate: action.payload.navigate,
      location: action.payload.location,
    }),
  );

export const makePaymentEpic = (action$, state$) =>
  action$.ofType(INITIATE_PAYMENT).pipe(
    RxOp.flatMap((action) => {
      const isDatacap = isDatacapTransaction(
        action.payload.payment_tender_type,
        action.payload?.cc_reader_type,
      );

      // Resume polling: if a previous DPS attempt timed out (indeterminate),
      // check its status before starting a new payment to avoid duplicates.
      const pendingPAUI = getPendingPAUI();
      if (pendingPAUI && isDatacap) {
        const proceedWithNewPayment = () => {
          // Tombstone the attempt (forces fresh trace), then clear the
          // indeterminate flag. Both touch disjoint localStorage fields
          // so order is safe, but the sequence reflects intent:
          // resolve first, then clean up.
          markResponseReceived({ lastSeenPAUI: pendingPAUI });
          clearPendingPAUI();
          return Rx.from(
            makePayment(makePaymentParams(state$.value, action)),
          ).pipe(
            RxOp.flatMap((newResponse) =>
              handleMakePaymentResponse(newResponse, action, state$, isDatacap),
            ),
            RxOp.catchError((err) =>
              handleMakePaymentError(err, action, state$, isDatacap),
            ),
          );
        };

        return Rx.from(
          pollByPaymentIdentifier({
            url: getPosApiUrl(state$.value),
            token: getAccessToken(state$.value),
            paymentAttemptUniqueId: pendingPAUI,
            method: 'GET',
          }),
        ).pipe(
          RxOp.flatMap((response) => {
            if (response.data?.status === 'successful') {
              // Previous attempt succeeded — show result and clear all
              // trace state (including pendingPAUI) via full wipe.
              clearTraceStateFromStorage();
              return Rx.of(
                paymentSuccess(response),
                getPaymentByIdSuccess(response),
                resetPinPadRequest(
                  state$.value.terminalLogin.terminalSession.terminal_id,
                ),
                getUpdatedCartRequest({
                  id: state$.value.cart.order.order_id,
                  navigate: action.payload.navigate,
                  location: action.payload.location,
                }),
                checkoutAlertBarAction(
                  replaceAlerts({
                    heading: 'Payment Status:',
                    text: 'A previous card payment completed successfully.',
                    variant: 'success',
                  }),
                ),
              );
            } else if (response.data?.status === 'failed') {
              // Previous attempt failed — write tombstone and proceed
              return proceedWithNewPayment();
            } else {
              // Still pending — warn cashier, do not start a new payment
              console.warn(
                `Resume-poll: previous DPS attempt ${pendingPAUI} still pending. Next retry will skip resume-poll.`,
              );
              clearPendingPAUI();
              return Rx.of(
                checkoutModalAction(closeModal()),
                paymentError(
                  'A previous card payment is still being processed. Please wait before retrying.',
                ),
                checkoutAlertBarAction(
                  replaceAlerts({
                    heading: 'Payment Status:',
                    text: 'A previous card payment is still being processed. Please wait before retrying.',
                    variant: 'warn',
                  }),
                ),
              );
            }
          }),
          // Poll failed (404, network error) — old attempt is gone, proceed
          RxOp.catchError(() => proceedWithNewPayment()),
        );
      }

      return Rx.from(makePayment(makePaymentParams(state$.value, action))).pipe(
        RxOp.flatMap((response) =>
          handleMakePaymentResponse(response, action, state$, isDatacap),
        ),
        RxOp.catchError((err) =>
          handleMakePaymentError(err, action, state$, isDatacap),
        ),
      );
    }),
  );

/*
  Due to limitations of the pos-bridge and KD that won't be resolved until DAL,
  we must poll the getPaymentById endpoint to get the response from the check reader.

  This process is initiated by the INITIATE_CHECK_SCAN action, which triggers the scanCheckEpic:
  - the scanCheckEpic does the POST request to make a payment of payment_tender_type: "check"
  - a 200 response means that the websocket was opened in the bridge to listen for the physical check insertion
  - upon response, the countdown timer that is displayed in the Checkout modal is initiated with the maxAttempts (currently 30 second timer)

  A successful scanCheckEpic dispatches the IS_POLLING_FOR_CHECK_RESPONSE action, which triggers the checkScanPollingEpic that does the following:
  - Starts making requests to /api/v2/payments/{id} at a configurable polling interval, decrementing the 30 second timer each request
  - It will keep going at that interval until one of 3 things happen:
    a) the configurable maxAttempts is reached -> modal will close and a timeout error is displayed by the FE in an alert bar
    b) the status of the payment changes to 'successful' -> modal will close and the payment will process successfully
    c) the status of the payment changes to 'errored' -> modal will close and a payment error is displayed by the FE in an alert bar
*/

const oneSecond = 1000;
const maxAttempts = checkScanMaxAttempts; // poll for max of 30 seconds
const ADDITIONAL_CHECK_PROCESSING_ATTEMPTS = 15;

export const scanCheckEpic = (action$, state$) =>
  action$.ofType(INITIATE_CHECK_SCAN).pipe(
    RxOp.flatMap((action) =>
      Rx.from(makePayment(makePaymentParams(state$.value, action))).pipe(
        RxOp.flatMap((response) =>
          Rx.of(
            isPollingForCheckResponse({
              ...response,
              isPollingForCheckResponse: true,
              navigate: action.payload.navigate,
              location: action.payload.location,
            }),
            setInsertCheckTimeRemaining(checkScanMaxAttempts),
          ),
        ),
        RxOp.catchError((err) =>
          caughtCheckoutError(state$.value)(
            err,
            makeCheckPaymentError(err.message),
            getUpdatedCartRequest({
              id: state$.value.cart.order.order_id,
              navigate: action.payload.navigate,
              location: action.payload.location,
            }),
          ),
        ),
      ),
    ),
  );

export const checkScanPollingEpic = (action$, state$) =>
  action$.ofType(IS_POLLING_FOR_CHECK_RESPONSE).pipe(
    RxOp.switchMap((action) =>
      Rx.timer(oneSecond, oneSecond).pipe(
        // fire once a second after a one second delay
        RxOp.takeUntil(action$.ofType(STOP_CHECK_SCAN_POLL)),
        RxOp.exhaustMap((attempts) => {
          attempts++;
          if (
            attempts > maxAttempts &&
            action.payload.data.status === TRANSACTION_STATUS.PENDING &&
            !state$.value.checkout.isPollingTimeExtended
          ) {
            return Rx.of(
              isPollingTimeExtended(true),
              setInsertCheckTimeRemaining(ADDITIONAL_CHECK_PROCESSING_ATTEMPTS),
            );
          }
          if (state$.value.checkout.timeRemainingToInsertCheck === 0) {
            return Rx.of(
              setCheckScanFeedback(null),
              stopCheckScanPoll(),
              checkoutAlertBarAction(
                replaceAlerts({
                  heading: `Payment Status:`,
                  text: 'Check Payment Error: Check read attempt timed out',
                  variant: 'error',
                }),
              ),
            );
          }
          return Rx.from(
            getPaymentById(getPaymentByIdParams(state$.value, action)),
          ).pipe(
            RxOp.flatMap((response) => {
              let variant =
                response.data.status === TRANSACTION_STATUS.SUCCESSFUL
                  ? 'success'
                  : 'error';
              let alertMessage = `${response.data.payment_type} ${response.data.status}`;
              if (response.data.status === TRANSACTION_STATUS.SUCCESSFUL) {
                if (response.data.keep_polling === true) {
                  return Rx.of(
                    !state$.value.checkout.isCheckProcessing
                      ? isCheckProcessing(true)
                      : EMPTY_ACTION,
                  );
                } else {
                  return Rx.of(
                    isCheckProcessing(false),
                    setCheckScanFeedback(null),
                    checkoutAlertBarAction(clearAlerts()), // clear any previous payment error alerts to avoid confusion
                    getPaymentByIdSuccess(response),
                    paymentSuccess(response),
                    stopCheckScanPoll(),
                    getUpdatedCartRequest({
                      id: state$.value.cart.order.order_id,
                      navigate: action.payload.navigate,
                      location: action.payload.location,
                    }),
                  );
                }
              } else if (response.data.status === TRANSACTION_STATUS.ERRORED) {
                return Rx.of(
                  getPaymentByIdError(response),
                  setCheckScanFeedback(null),
                  stopCheckScanPoll(),
                  checkoutAlertBarAction(
                    replaceAlerts({
                      heading: `Payment Status:`,
                      text: response.data.payment_response || alertMessage,
                      variant,
                    }),
                  ),
                );
              } else {
                // continue polling for a status update
                return Rx.of(
                  getPaymentByIdSuccess(response),
                  setCheckScanFeedback(response.data.check_scan_feedback),
                  setInsertCheckTimeRemaining(maxAttempts - attempts),
                );
              }
            }),
            RxOp.catchError((err) =>
              caughtCheckoutError(state$.value)(
                err,
                getPaymentByIdError(err.message),
                getUpdatedCartRequest({
                  id: state$.value.cart.order.order_id,
                  navigate: action.payload.navigate,
                  location: action.payload.location,
                }),
              ),
            ),
          );
        }),
      ),
    ),
  );

export const awaitCustomerCreditCardEpic = (action$, state$) =>
  action$.ofType(IS_AWAITING_CREDIT_CARD).pipe(
    RxOp.switchMap(() =>
      Rx.timer(oneSecond, oneSecond).pipe(
        // fire once a second after a one second delay
        RxOp.takeUntil(action$.ofType(STOP_AWAITING_CREDIT_CARD)),
        RxOp.exhaustMap((secondsElapsed) => {
          secondsElapsed++;
          if (secondsElapsed <= maxSecondsToInsertCard) {
            return Rx.of(
              setAwaitCreditCardTimeRemaining(
                maxSecondsToInsertCard - secondsElapsed,
              ),
            );
          } else {
            return Rx.of(
              checkoutModalAction(closeModal()),
              stopAwaitingCreditCard(),
              cancelTransactionRequest(cancelTransactionParams(state$.value)),
            );
          }
        }),
      ),
    ),
  );

export const creditCardCountdownEpic = (action$, _state$) =>
  action$.ofType(START_POLLING_FOR_CC_RESPONSE).pipe(
    RxOp.switchMap(() =>
      Rx.timer(0, oneSecond).pipe(
        RxOp.takeUntil(action$.ofType(STOP_POLLING_FOR_CC_RESPONSE)),
        RxOp.exhaustMap((secondsElapsed) => {
          secondsElapsed++;
          if (secondsElapsed <= maxSecondsToInsertCardDps) {
            return Rx.of(
              setAwaitCreditCardTimeRemaining(
                maxSecondsToInsertCardDps - secondsElapsed,
              ),
            );
          } else {
            return Rx.of(EMPTY_ACTION);
          }
        }),
      ),
    ),
  );

const cancelTransactionParams = (state) => ({
  url: getPosApiUrl(state),
  token: getAccessToken(state),
  method: 'POST',
  // This is hardcoded to fix the credit card cancel button on the
  // processing modal. This functionality is needed for Datacap/Trancloud
  // Certification.  There are other stores to fix the check reader
  // cancel button on the processing modal that should make this dynamic.
  data: {
    payment_type: 'credit_card',
    payment_attempt_unique_identifier: state?.checkout?.paymentAttemptUniqueId,
  },
});
export const cancelTransactionEpic = (action$, state$) =>
  action$.ofType(CANCEL_TRANSACTION_REQUEST).pipe(
    RxOp.flatMap(() =>
      Rx.from(cancelTransaction(cancelTransactionParams(state$.value))).pipe(
        RxOp.flatMap((response) => {
          const paymentAttemptUniqueId =
            state$.value.checkout?.paymentAttemptUniqueId;

          if (!paymentAttemptUniqueId) {
            const responseStatus = response.status;
            const variant = responseStatus === 'successful' ? 'info' : 'error';
            return Rx.of(
              cancelTransactionSuccess(),
              state$.value.checkout?.isAwaitingCreditCard
                ? stopAwaitingCreditCard()
                : EMPTY_ACTION,
              checkoutAlertBarAction(
                replaceAlerts({
                  heading: `Cancel Transaction Status:`,
                  text: `${responseStatus}`,
                  variant: variant,
                }),
              ),
            );
          } else {
            // for datacap payments, we'll use the polling response to confirm cancellation
            return Rx.of(EMPTY_ACTION);
          }
        }),
        RxOp.catchError((err) =>
          caughtCancelError(state$.value)(err, cancelTransactionFailure()),
        ),
      ),
    ),
  );

const getServiceFeeParams = (state, payload) => ({
  url: getPosApiUrl(state),
  token: getAccessToken(state),
  amount: payload.amount,
});

export const getServiceFeeEpic = (action$, state$) =>
  action$.ofType(GET_SERVICE_FEE).pipe(
    RxOp.flatMap((action) =>
      Rx.from(
        fetchServiceFeeRequest(
          getServiceFeeParams(state$.value, action.payload),
        ),
      ).pipe(
        RxOp.flatMap((response) => Rx.of(getServiceFeeSuccess(response.data))),
        RxOp.catchError((err) =>
          caughtCheckoutError(state$.value)(
            err,
            getServiceFeeError(err.message),
          ),
        ),
      ),
    ),
  );

const getConvenienceFeeParams = (state, payload) => ({
  url: getPosApiUrl(state),
  token: getAccessToken(state),
  agency_id: payload.agency_id,
});

export const getConvenienceFeeEpic = (action$, state$) =>
  action$.ofType(GET_CONVENIENCE_FEE).pipe(
    RxOp.flatMap((action) =>
      Rx.from(
        fetchConvenienceFeeRequest(
          getConvenienceFeeParams(state$.value, action.payload),
        ),
      ).pipe(
        RxOp.flatMap((response) =>
          Rx.of(getConvenienceFeeSuccess(response.data)),
        ),
        RxOp.catchError((err) =>
          caughtCheckoutError(state$.value)(
            err,
            getConvenienceFeeError(err.message),
          ),
        ),
      ),
    ),
  );

const MS_CC_POLLING_INTERVAL = 2000;

export const creditCardReadPollingEpic = (action$, state$) =>
  action$.ofType(START_POLLING_FOR_CC_RESPONSE).pipe(
    RxOp.switchMap((action) =>
      Rx.timer(0, MS_CC_POLLING_INTERVAL).pipe(
        RxOp.takeUntil(action$.ofType(STOP_POLLING_FOR_CC_RESPONSE)),
        RxOp.scan((acc) => acc + MS_CC_POLLING_INTERVAL, 0),
        RxOp.exhaustMap((elapsedTime) => {
          if (elapsedTime >= maxSecondsToInsertCardDps * 1000) {
            // Mark the attempt as indeterminate so the next payment attempt
            // can resume polling for this PAUI before starting a new payment.
            const paui = state$.value.checkout.paymentAttemptUniqueId;
            if (paui) {
              markAttemptIndeterminate({ pendingPAUI: paui });
            } else {
              console.warn(
                'creditCardReadPollingEpic: timeout but paymentAttemptUniqueId is null; cannot mark indeterminate',
              );
            }
            return Rx.of(
              checkoutModalAction(closeModal()),
              cancelTransactionRequest(cancelTransactionParams(state$.value)),
              checkoutAlertBarAction(
                replaceAlerts({
                  heading: 'Payment Status:',
                  text: 'Card Payment Error: Card Payment attempt timed out',
                  variant: 'error',
                }),
              ),
              stopPollingForCCResponse(),
            );
          } else {
            return Rx.from(
              pollByPaymentIdentifier(
                pollByPaymentIdentifierParams(state$.value),
              ),
            ).pipe(
              RxOp.flatMap((response) => {
                if (response.data?.status === 'successful') {
                  // Clear all trace state on DPS success (mirrors legacy-path cleanup)
                  clearTraceStateFromStorage();
                  return Rx.of(
                    checkoutAlertBarAction(clearAlerts()), // clear any previous payment error alerts to avoid confusion
                    pollingCCSuccess(),
                    paymentSuccess(response),
                    getPaymentByIdSuccess(response),
                    stopPollingForCCResponse(),
                    resetPinPadRequest(
                      state$.value.terminalLogin.terminalSession.terminal_id,
                    ),
                    getUpdatedCartRequest({
                      id: state$.value.cart.order.order_id,
                      navigate: action.payload?.navigate,
                      location: action.payload?.location,
                    }),
                  );
                } else if (response.data?.status === 'failed') {
                  // DPS tombstone: record that polling received a failure response
                  markResponseReceived({
                    lastSeenPAUI: state$.value.checkout.paymentAttemptUniqueId,
                  });
                  return Rx.of(
                    checkoutModalAction(closeModal()),
                    pollingCCError(),
                    paymentError(response.data?.payment_response || 'Error'),
                    stopPollingForCCResponse(),
                    resetPinPadRequest(
                      state$.value.terminalLogin.terminalSession.terminal_id,
                    ),
                    checkoutAlertBarAction(
                      replaceAlerts({
                        heading: 'Payment Status:',
                        text: `Card Payment: ${response.data?.payment_response ?? 'Error'}`,
                        variant: 'error',
                      }),
                    ),
                  );
                } else {
                  return Rx.of(EMPTY_ACTION);
                }
              }),
              RxOp.catchError((err) =>
                caughtCheckoutError(state$.value)(
                  err,
                  stopPollingForCCResponse(),
                  pollingCCError(),
                  paymentError(err.message),
                  resetPinPadRequest(
                    state$.value.terminalLogin.terminalSession.terminal_id,
                  ),
                  getUpdatedCartRequest({
                    id: state$.value.cart.order.order_id,
                    navigate: action.payload.navigate,
                    location: action.payload.location,
                  }),
                ),
              ),
            );
          }
        }),
      ),
    ),
  );
